import { PREVIEWABLE_IMAGE_EXTENSIONS, UploadedFile } from '@99designs/design-services-common';
import { StructuredBriefFile } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';

const MAX_FILE_SIZE = 10000000; // 10MB
const MAX_FILE_NAME_LENGTH = 16;

export function mapErrorMessages(allowedFileTypes: string[]): string {
    if (allowedFileTypes.length === 0) {
        return __('Only files under 10MB are supported');
    }

    const allowedTypes = [...allowedFileTypes].map((ext) => ext.replace('.', ''));

    if (allowedFileTypes.length === 1) {
        return __('Only {{fileType}} files under 10MB are supported', {
            fileType: allowedTypes[0],
        });
    }

    const lastFileType = allowedTypes.pop();
    const fileTypes = allowedTypes.join(', ');
    return __('Only {{fileTypes}} and {{lastFileType}} files under 10MB are supported', {
        fileTypes,
        lastFileType,
    });
}

export function filterFiles(files: FileList | null, allowedFileTypes: string[]) {
    if (!files) {
        return [];
    }

    if (allowedFileTypes.length === 0) {
        return Array.from(files).filter((file) => file.size < MAX_FILE_SIZE);
    }

    return Array.from(files).filter((file) => {
        const fileExtension = file.name.split('.').pop();
        return allowedFileTypes.includes(`.${fileExtension}`) && file.size < MAX_FILE_SIZE;
    });
}

/**
 * Map the response from the MCP upload bucket to the StructuredBriefFile type.
 *
 * @param files {UploadedFile[]} the files to map
 */
export function mapUploadFilesToBriefFiles(files: UploadedFile[]): StructuredBriefFile[] {
    return files.map((file) => {
        const isPreviewable = PREVIEWABLE_IMAGE_EXTENSIONS.includes(
            file.name.split('.').pop()?.toLowerCase() || ''
        );

        return {
            __typename: 'StructuredBriefFile',
            size: parseInt(file.originalFileSize) || 0, // Provide a default value in case of parsing error.
            bucket: 'CimpressDocs',
            key: file.id,
            name: file.name,
            downloadUrl: file.downloadUrl,
            previewUrl: isPreviewable ? file.previewUrl : null,
            previewable: isPreviewable,
        };
    });
}

/**
 * Get a unique key for a file based on its name, size and last modified date.
 *
 * ## Notes
 *
 * This might not be as reliable as a hash of the binary content, but the use case is only to
 * track and remove files from the "uploading" state once they are added to the "uploaded" state.
 *
 * Even on collision, this will only cause issues if the user uploads a file, then uploads another
 * file with the colliding key before the first file is fully uploaded.
 *
 * The odds of this happening are low, and the impact is minimal.
 *
 * @param file {File} the file that is uploading
 */
export function mapFileToFileKey(file: File) {
    return `${file.name}-${file.size}-${file.lastModified}`;
}

/**
 * Truncate a filename, leaving always the extension, first three characters and last three
 * characters. If there is enough space, it will add more characters at the front.
 *
 * @param filename {string} the filename to truncate
 */
export function truncateFileName(filename: string) {
    if (filename.length < MAX_FILE_NAME_LENGTH) {
        return filename;
    }

    const filenameParts = filename.split('.');
    const extension = filenameParts.pop() || '';
    const filenameSansExtension = filenameParts.join('.');

    const truncatedNameFirstPart = filenameSansExtension.slice(
        0,
        MAX_FILE_NAME_LENGTH - extension.length - 6
    );
    const truncatedNameLastPart = filenameSansExtension.slice(-3);
    const truncatedName = `${truncatedNameFirstPart}...${truncatedNameLastPart}`;

    return `${truncatedName}.${extension}`;
}
